import { Suspense, useCallback, useEffect, useMemo } from 'react';
import useStepper from 'hooks/useStepper';
import type { StepType } from 'components/layout';
import { FormNavigation, StepperPaper } from 'components/layout';
import AppPlatformForm from './AppPlatformForm';
import { FormProvider, useForm } from 'react-hook-form';
import AdAccountsForm from './AdAccountsForm';
import CampaignsForm from './CampaignsForm';
import AdSetsForm from './AdSetsForm';
import CreativesForm from './CreativesForm';
import AdForm from './AdForm';
import useJobStatusCheck from 'hooks/useJobStatusCheck';
import type { CreateFacebookAdResponse } from '../api/createFacebookAd';
import { useCreateFacebookAd } from '../api/createFacebookAd';
import queryKeys from '../api/queryKeys';
import queryKeysCreatives from '../../creatives/api/queryKeys';
import { AdCreationProgress, AddPackButton, CircularProgress, ConfirmationDialog } from 'components/common';
import AddTestingPackButton from './AddTestingPackButton';
import { JobResultTable } from './FacebookFormComponents';
import { queryClient } from 'lib/react-query';
import type { CreativePayloadEntry } from '../api/types';
import { useCampaigns } from '../api';
import { useEnqueueSnackbar } from 'hooks';
import type { CreativeEntry } from 'types/shared';
import type { AdSetEntry } from '../api/getAdSets';
import { uniqBy } from 'lodash-es';
import { useIsFetching } from '@tanstack/react-query';
import type { CreativePackEntry } from '../types';
import facebookValidator from '../validation/facebookValidator';
import { CAMPAIGN_NAME_FILTER } from '../constants';
import type { z } from 'zod';
import { useMe } from 'features/login';
import AdSetBudgetCounter from './AdSetBudgetCounter';
import { APP_LIST } from '../../../constants';

type FormData = z.infer<typeof facebookValidator.schema>;

const FacebookForm = () => {
  const { data: me } = useMe();
  const { onShowErrorAlert } = useEnqueueSnackbar();

  const initialSteps = useMemo<StepType[]>(
    () => [
      { title: 'App & Platform', component: AppPlatformForm },
      { title: 'Ad Accounts', component: AdAccountsForm },
      { title: 'Creatives', component: CreativesForm },
      { title: 'Campaigns', component: CampaignsForm },
      { title: 'Ad Sets', component: AdSetsForm },
      { title: 'Ad', component: AdForm },
    ],
    [],
  );
  const { currentStep, resetSteps, prevStep, nextStep, StepComponent } =
    useStepper({ initialSteps, defaultStep: 0 });

  const adSettingsIsFetching = useIsFetching({
    queryKey: queryKeys.adSettings,
  });

  const getDefaultValues = useMemo(() => {
    const availableApps = APP_LIST.map(a => String(a.code));
    const matchedProjects = me?.apps.filter((myApp) => availableApps.includes(myApp));

    return {
      currentStep: 0,
      app: matchedProjects ? matchedProjects[0] : undefined,
      platform: 'WEB',
      marketingProcess: undefined,
      adType: '',
      adAccounts: [],
      selectedCampaigns: [],
      selectedCampaignsOnly: false,
      selectedCampaignsBudgets: {},
      selectedAdSets: [],
      selectedAdSetsBudgets: {},
      selectedAdSetsDuplicatesCount: {},
      selectedAdSetsStartTimes: {},
      selectedCreatives: [],
      packsWithCreatives: [
        {
          tag: '',
          adType: '',
          creatives: [],
          filterCreativeByName: [],
          visual_touch_ups: true,
        },
      ],
      activePacksWithCreatives: 0,
      selectedAdSettings: {},
      selectedConversionDomain: '',
      adLabelSuffix: '',
      callToActions: 'LEARN_MORE',
      hotjar: false,
      filterCampaignsByName: [],
      filterAdSetsByName: [],
      activePackTag: '',
    };
  }, [me]);

  const formMethods = useForm<FormData>({
    resolver: facebookValidator.resolver,
    defaultValues: getDefaultValues,
  });

  const {
    setValue,
    trigger,
    reset: resetForm,
    handleSubmit,
    formState: { isValid, isSubmitting, isSubmitted },
  } = formMethods;

  useEffect(() => {
    setValue('currentStep', currentStep, { shouldValidate: true });
  }, [currentStep, setValue]);

  const {
    data: jobStatusResponse,
    runJubStatusCheck,
    isLoading: isJobStatusChecking,
    isFinished: isJobStatusFinished,
    clearJobStatus,
  } = useJobStatusCheck();

  const createFacebookAd = useCreateFacebookAd({
    config: {
      onSuccess: (r: CreateFacebookAdResponse) => {
        runJubStatusCheck(r.job_id);
      },
      onError: (err) => {
        if (err.cause) {
          onShowErrorAlert(
            (err.cause as { details?: string })?.details ?? 'Error occur',
            { delayAlert: 3500 },
          );
          console.error(err.cause);
        }
      },
    },
  });

  const onSubmit = useCallback(
    (data: FormData) => {
      try {
        if (!isValid) return;

        const isMarketingProcessInbox =
          data.marketingProcess && data.marketingProcess?.includes('inbox');

        const cachedCreativesData = queryClient
          .getQueryCache()
          .findAll({ queryKey: queryKeysCreatives.allCreatives })
          .flatMap((query) => query.state.data as CreativeEntry[])
          .filter((c) => !!c);

        const getCreativePacks = (): CreativePackEntry[] => {
          return data.packsWithCreatives.map(
            ({ creatives: items, tag, visual_touch_ups, adType: packAdType }) => ({
              visual_touch_ups,
              tag,
              ad_type: packAdType,
              creatives: items.map((packId) => {
                const creative = cachedCreativesData.find(
                  (c) => c.id === packId,
                ) as CreativePayloadEntry;
                return {
                  id: creative.id,
                  creative_id: creative.creative_id,
                  name: creative.name,
                  creative_type: creative.creative_type,
                };
              }),
            }),
          );
        };

        const getAdSets = (): AdSetEntry[] | null => {
          const cachedAdSetsData = queryClient
            .getQueryCache()
            .findAll({ queryKey: queryKeys.adSets })
            .flatMap((query) => query.state.data as AdSetEntry[])
            .filter((c) => !!c);

          const uniqAdSets = data.selectedAdSets
            ? uniqBy(cachedAdSetsData, 'id').filter((c) =>
              data.selectedAdSets?.find(({ id }) => id === c.id),
            )
            : null;

          return uniqAdSets
            ? (uniqAdSets.map((adSet) => {
              const campaignStatus = data.selectedCampaigns?.find(
                ({ id }) => id === adSet.campaign_id,
              )?.status;

              const status = data.selectedAdSets?.find(
                ({ id }) => id === adSet.id,
              )?.status;

              const budget = adSet?.is_cbo
                ? Number(data.selectedCampaignsBudgets?.[adSet.campaign_id] ?? '')
                : Number(data.selectedAdSetsBudgets?.[adSet.id] ?? '');

              // If marketing process is inbox, we do not need duplicates count or start time
              const duplicatesCount = !isMarketingProcessInbox ? {
                duplicates_count: Number(data.selectedAdSetsDuplicatesCount?.[adSet.id]),
              } : undefined;

              const startTime = !isMarketingProcessInbox
                ? data.selectedAdSetsStartTimes?.[adSet.id] || undefined
                : undefined;

              return {
                ...adSet,
                ...duplicatesCount,
                ...(startTime ? { start_time: startTime } : {}),
                budget,
                status,
                campaign_status: campaignStatus,
              };
            }) as AdSetEntry[])
            : null;
        };

        createFacebookAd.mutate({
          app: data.app,
          platform: data.platform,
          call_to_action: data.callToActions,
          creative_packs: getCreativePacks(),
          ad_sets: getAdSets(),
          hotjar: data.hotjar,
          conversion_domain: data.selectedConversionDomain,
          ad_settings: data.selectedAdSettings,
          optional_ad_label: data.adLabelSuffix,
        });
      } catch (error) {
        console.error('Error during Facebook ad creation:', error);
        onShowErrorAlert('Error during Facebook ad creation', {
          delayAlert: 3500,
        });
      }
    },
    [isValid, onShowErrorAlert, createFacebookAd],
  );

  const adAccounts: string[] | undefined = formMethods.watch('adAccounts');
  const marketingProcess: string = formMethods.watch('marketingProcess');
  const isMarketingProcessHypothesisTesting = marketingProcess === 'hypothesis_testing';

  useCampaigns({
    params: {
      facebookAccountId: adAccounts,
      campaignNameFilter:
        CAMPAIGN_NAME_FILTER[marketingProcess as keyof typeof CAMPAIGN_NAME_FILTER] ?? '',
    },
    enabled: !!adAccounts?.length && !!marketingProcess,
  });

  const handleNextStep = useCallback(async () => {
    const valid = await trigger();
    if (!valid || isSubmitting || isSubmitted) return;

    if (currentStep === initialSteps.length - 1) {
      void handleSubmit(onSubmit)();
    } else {
      nextStep();
    }
  }, [
    nextStep,
    trigger,
    handleSubmit,
    onSubmit,
    initialSteps,
    currentStep,
    isSubmitting,
    isSubmitted,
  ]);

  const handlePrevStep = useCallback(() => {
    prevStep();
    void trigger();
  }, [prevStep, trigger]);

  const handleRestart = useCallback(() => {
    resetForm();
    resetSteps();
    clearJobStatus();
  }, [resetForm, resetSteps, clearJobStatus]);

  const handleLaunchMoreAds = useCallback(() => {
    window.open('/facebook', '_blank', 'noopener');
  }, []);

  const isStillFetching = isSubmitted || !!isJobStatusChecking;
  const isDisabledNext = !!adSettingsIsFetching || isStillFetching || !isValid;

  return (
    <StepperPaper
      title={'Facebook Ads Automation'}
      steps={initialSteps}
      activeStep={currentStep}
    >
      <FormProvider {...formMethods}>
        {isJobStatusChecking ? (
          <AdCreationProgress onLaunchMoreAds={handleLaunchMoreAds} />
        ) : (
          <Suspense fallback={<CircularProgress />}>
            {isJobStatusFinished ? (
              <JobResultTable rows={jobStatusResponse?.result} />
            ) : (
              <StepComponent />
            )}
          </Suspense>
        )}

        <FormNavigation
          isJobStatusFinished={isJobStatusFinished}
          onRestart={handleRestart}
          isDisabledRestart={!currentStep || isStillFetching}
          onBack={handlePrevStep}
          isDisabledBack={!currentStep || isStillFetching}
          onNext={handleNextStep}
          isDisabledNext={isDisabledNext}
          titleNext={
            currentStep === initialSteps.length - 1 ? 'Create Ads' : 'Next'
          }
          childrenOnTheRightSide={
            <>
              <AdSetBudgetCounter />
              {currentStep === 2
                ? isMarketingProcessHypothesisTesting
                  ? <AddTestingPackButton />
                  : <AddPackButton />
                : null}
            </>
          }
        />
        {!jobStatusResponse?.result && (
          <ConfirmationDialog
            isUnblock={createFacebookAd.isSuccess && isJobStatusFinished}
          />
        )}
      </FormProvider>
    </StepperPaper>
  );
};
export default FacebookForm;
